import React, { ChangeEvent } from 'react';
import { Alert, Box, Button, Grid, TextField, Typography } from '@mui/material';
import { ValidationErrors } from 'src/constants/validations';
import { SignupAccountFormValues } from '../../controllers/SignupAccountViewController';
import { noticeSx } from '../../helpers/sx';

type Props = {
  formValues: SignupAccountFormValues;
  errors: ValidationErrors;
  formError: string | null;
  submitting: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => void;
  onSubmit: () => void;
};

export const SignupAccountView: React.FC<Props> = ({
  formValues,
  errors,
  formError,
  submitting,
  onChange,
  onSubmit,
}) => {
  const field = (
    name: keyof SignupAccountFormValues,
    label: string,
    extra: object = {},
    hint = '',
  ) => (
    <TextField
      fullWidth
      name={name}
      label={label}
      value={formValues[name]}
      onChange={onChange}
      error={errors[name] != null}
      helperText={errors[name] ?? hint}
      inputProps={{ 'data-testid': `signup-${name}` }}
      {...extra}
    />
  );

  return (
    <Box maxWidth={560} mx="auto">
      <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
        Create your account
      </Typography>
      {/*
        Says what the next screen will be, because this one asks for a password and then appears to do
        nothing with it: the code comes first, and the company details after that.
      */}
      <Typography variant="body1" color="text.secondary" mb={4}>
        We will email you a code to confirm this address, and ask about your company after that.
      </Typography>

      {formError != null && (
        <Alert severity="error" sx={noticeSx}>
          {formError}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>{field('firstName', 'First name')}</Grid>
        <Grid item xs={12} sm={6}>{field('lastName', 'Last name')}</Grid>
        <Grid item xs={12}>{field('email', 'Your email')}</Grid>
        <Grid item xs={12}>
          {field('password', 'Password', { type: 'password' }, 'At least 8 characters.')}
        </Grid>
        <Grid item xs={12}>
          {field('phone', 'Business phone', {}, 'We will use this as your business number with the carriers.')}
        </Grid>
      </Grid>

      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button variant="contained" size="large" disabled={submitting} onClick={onSubmit}>
          {submitting ? 'Saving…' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
};
